import {Camera} from 'src/services/api/endpoints/CameraEndpoint'
import axios from './_axios'
import {prepareParams} from './_internal'
import {AxiosResult, Paginate} from './_types'
import {Company} from './CompanyEndpoint'

const prefix = process.env.REACT_APP_COMPANIES_BASE_URL

export type ClientInclude = 'company' | 'client.cameras' | 'cameraStatus' | 'cameras' | 'moniClientInfo' | 'analyticCameras' | 'onePortariaClientInfo'

export type Client = {
  id: number
  name: string
  email: string
  sigmaAccount?: string
  sigmaClientId: null | number
  sigmaLastCheckedDate: string
  isDeleted: boolean
  companyId: number
  cameraStatusId: number
  cameras: Camera[]
  moniClientInfo?: MoniClientInfo | null
  origin?: string
  defaultLayoutId?: number
  isClientDefault: boolean
  totalOfCameras: number
  onePortariaClientInfo?: OnePortariaInfo | null
  company?: Company
  daysToRetainStream: number
  daysToRetainImagens: number
  daysToRetainEvents: number
}

export type CreateOrEditClient = {
  clientName: string
  email?: string | null
  sigmaAccount?: string | null
  companyId?: number
  cameraStatusId?: number
  moniClientInfo?: MoniClientInfo | null
  onePortariaClientInfo?: OnePortariaInfo | null
  defaultLayoutId?: number
  daysToRetainStream?: number
  daysToRetainImagens?: number
  daysToRetainEvents?: number
}

export class PatchClient {
  id: number
  clientName?: string
  email?: string | null
  sigmaAccount?: string | null
  moniClientInfo?: MoniClientInfo | null
  onePortariaClientInfo?: OnePortariaInfo | null
  defaultLayoutId?: number

  constructor(id: number, client: CreateOrEditClient) {
    this.id = id
    this.clientName = client.clientName
    this.email = client.email
    this.sigmaAccount = client.sigmaAccount
    this.moniClientInfo = client.moniClientInfo
    this.onePortariaClientInfo = client.onePortariaClientInfo
    this.defaultLayoutId = client.defaultLayoutId
  }
}

export type MoniClientInfo = {
  clientCode?: string
  moniCompanyId?: string
  sector?: string
}

export type OnePortariaInfo = {
  clientCode?: string
  sector?: number
  clientId?: number
  companyCode?: string
}

export type GetClientsParams = {
  page?: number
  pageSize?: number
  includes?: ClientInclude[]
  paginate?: boolean
  filter?: {
    ids?: number[]
    companyIds?: number[]
    sigmaAccountIds?: number[]
    serverTypeId?: number
  }
  search?: {
    name?: string
  }
}

export type DispatchTestEventResponse = {
  status: number
  devMessage: string
  message: string
  response?: any
}

export type DispatchTestEvent = {
  code: string
  companyId: number
}

export class PatchDaysToRetain {
  id: number
  daysToRetainStream?: number
  daysToRetainImagens?: number
  daysToRetainEvents?: number

  constructor(id: number, client: CreateOrEditClient) {
    this.id = id
    this.daysToRetainStream = client.daysToRetainStream
    this.daysToRetainImagens = client.daysToRetainImagens
    this.daysToRetainEvents = client.daysToRetainEvents
  }
}
export class ClientEndpoint {
  async getMany(params: GetClientsParams): Promise<AxiosResult<Paginate<Client>>> {
    return await axios.get(`${prefix}/clients`, {
      params: prepareParams(params),
    })
  }

  async getById(clientId: number): Promise<AxiosResult<Client>> {
    return await axios.get(`${prefix}/client`, {params: {id: clientId}})
  }

  async createOne(body: CreateOrEditClient): Promise<AxiosResult<any>> {
    return await axios.post(`${prefix}/client`, body)
  }

  async updateOne(body: PatchClient): Promise<AxiosResult<any>> {
    return await axios.patch(`${prefix}/client`, body)
  }

  async updateDaysToRetain(body: PatchDaysToRetain): Promise<AxiosResult<any>> {
    return await axios.patch(`${prefix}/client/days-to-retain`, body)
  }

  async deleteOne(clientId: number): Promise<AxiosResult<any>> {
    return await axios.delete(`${prefix}/client`, {params: {id: clientId}})
  }

  async deleteMany(clientIds: number[]): Promise<Promise<AxiosResult<any>>[]> {
    return clientIds.map(async (clientId) => {
      return await this.deleteOne(clientId)
    })
  }

  async dispatchTestEvent(body: DispatchTestEvent): Promise<AxiosResult<DispatchTestEventResponse>> {
    return await axios.post(`${prefix}/client/test-event`, body)
  }
}
